import { DialogPanel, DialogTitle } from '@headlessui/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY, EMPTY_FUNCTION } from '../../../lib/globals';
import { getTestProps } from '../../../lib/helpers';

import { CloseIcon } from '../../../images/shapes';

import Button from '../../Button/Button';
import LinkButton from '../../LinkButton/LinkButton';

const sizeClass = {
  sm: 'w-full max-w-[300px]',
  md: 'w-full max-w-[500px]',
  lg: 'w-full max-w-[700px]',
  xl: 'w-full max-w-[900px]',
  '2xl': 'w-full max-w-[1052px] m-1 md:m-4',
  '3xl': 'w-full max-w-[1240px] m-1 md:m-4',
  page: 'w-full max-w-[calc(100%-4rem)] sm:max-w-[calc(100%-7rem)] lg:max-w-[calc(100%-20rem)] h-screen',
};

const ModalPanel = ({
  onClose = EMPTY_FUNCTION,
  buttons = EMPTY_ARRAY,
  title = '',
  content = '',
  size = 'md',
  hideClose = false,
  dialogAdditionalClasses = '',
  contentAdditionalClasses = '',
  testId = '',
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 overflow-y-auto overflow-y-overlay"
      {...getTestProps(testId, 'container')}
    >
      <div
        className={twMerge(
          'flex min-h-full text-center',
          size === 'page'
            ? 'justify-end'
            : 'items-center justify-center sm:p-0 p-4',
        )}
      >
        <DialogPanel
          className={twMerge(
            'relative bg-white dark:bg-gray-900 text-left ',
            'overflow-hidden shadow-xl text-black',
            'ease-in duration-200 backdrop-blur-none',
            '',
            sizeClass[size || 'md'],
            size !== 'page'
              ? 'max-h-[calc(100vh-5rem)] md:max-h-[calc(100vh-3rem)] rounded-lg ' +
                  'data-[closed]:scale-95 data-[closed]:opacity-0 data-[closed]:translate-y-4'
              : 'max-h-screen bg-blue-50 data-[closed]:translate-x-full data-[closed]:opacity-0',
            dialogAdditionalClasses,
          )}
          transition
        >
          {!hideClose && (
            <div className="absolute top-0 right-0 z-20">
              <button
                type="button"
                className="rounded-md text-zinc-600 hover:text-gray-500 focus:outline-none
                pt-3 pr-3 sm:pt-7 sm:pr-6"
                onClick={onClose}
                {...getTestProps(testId, 'x-icon')}
              >
                <span className="sr-only">{t('Global.CloseModal')}</span>
                <CloseIcon
                  className="w-2.5 sm:w-3.5 h-2.5 sm:h-3.5 text-indigo-950 dark:text-white hover:text-gray-300"
                  aria-hidden="true"
                />
              </button>
            </div>
          )}

          <div className="h-full" {...getTestProps(testId, 'body')}>
            {title && (
              <DialogTitle
                className={twMerge(
                  'leading-none flex items-center dark:text-white h-[4rem]',
                )}
              >
                <div className="w-full font-medium px-6 md:px-11 py-2 dark:text-white">
                  {title}
                </div>
              </DialogTitle>
            )}
            {content && (
              <div
                className={twMerge(
                  'flex flex-col items-center justify-start',
                  'overflow-y-auto w-full scrollbar-sm',
                  title ? 'pt-3' : 'pt-7',
                  size !== 'page'
                    ? 'max-h-[calc(100vh-7rem)] px-6 md:px-11 pb-20'
                    : 'max-h-screen pt-0',
                  contentAdditionalClasses,
                )}
              >
                <div className="w-full h-full dark:text-gray-200">
                  {content}
                </div>
              </div>
            )}
          </div>
          {buttons && buttons.length > 0 && (
            <div
              className="w-full fixed left-0 bottom-0 flex items-center justify-center p-3 space-x-5
              border-t border-gray dark:border-slate-800 bg-white dark:bg-gray-900"
              {...getTestProps(testId, 'buttons')}
            >
              {buttons.map((button) => {
                const ButtonComponent = button.link ? LinkButton : Button;
                return (
                  <ButtonComponent
                    key={button.key}
                    buttonSize="sm"
                    buttonColor={button.color}
                    onClick={button.onClick}
                    additionalClasses={button.additionalClasses}
                    iconImage={button.iconImage}
                    iconPosition={button.iconPosition}
                    disabled={button.disabled}
                    testId={button.testId}
                    {...(button.link ? { link: button.link } : {})}
                    {...button.props}
                  >
                    {button.label}
                  </ButtonComponent>
                );
              })}
            </div>
          )}
        </DialogPanel>
      </div>
    </div>
  );
};

ModalPanel.propTypes = {
  /**
   * On close callback
   */
  onClose: PropTypes.func,
  /**
   * Modal dialog title
   */
  title: PropTypes.node,
  /**
   * Modal dialog subtitle
   */
  content: PropTypes.node,
  /**
   * Buttons under the dialog text
   */
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      onClick: PropTypes.func,
      additionalClasses: PropTypes.string,
      testId: PropTypes.string,
      color: PropTypes.string,
      iconImage: PropTypes.node,
      disabled: PropTypes.bool,
      key: PropTypes.string.isRequired,
    }),
  ),
  /**
   * Modal dialog subtitle
   */
  testId: PropTypes.string,
  /**
   * Modal size
   */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', '2xl', '3xl']),
  /**
   * If close button should be hidden
   */
  hideClose: PropTypes.bool,
  /**
   * Modal dialog additional classes
   */
  dialogAdditionalClasses: PropTypes.string,
  /**
   * Content additional classes
   */
  contentAdditionalClasses: PropTypes.string,
};

export default ModalPanel;
